/* FF UI primitives + small enhancements
   Safe to load alongside stock theme.js.
   Only touches [data-ff-*] hooks + wraps .prose tables.
   + supports deferred secondary product-card images (defer_secondary) */

(function () {
  let ffIdCounter = 0;
  function nextId(prefix) {
    ffIdCounter += 1;
    return prefix + "-" + ffIdCounter;
  }

  // Query selectorAll + include root if it matches
  function qsa(root, selector) {
    const scope = root && root.querySelectorAll ? root : document;
    const nodes = Array.from(scope.querySelectorAll(selector));
    if (scope !== document && scope.nodeType === 1 && scope.matches && scope.matches(selector)) {
      nodes.unshift(scope);
    }
    return nodes;
  }

  const FF_INIT_SELECTOR = [
    ".prose table",
    "product-card[data-ff-secondary-deferred]",
    "[data-ff-tabs]",
    "[data-ff-accordion]",
    "[data-ff-header]",
    "[data-ff-announce]",
    "[data-ff-main-product]",
    "[data-ff-backtotop]",
    "[data-ff-light-parallax]",
    "[data-ff-quick-access]",
    "[data-ff-tabs-parallax]",
    "[data-ff-parallax-multi-column]",
    "[data-ff-care-blog]",
    "[data-ff-richtext-collapsible]",
    "[data-ff-newsletter-popup-success]",
    "[data-ff-header-sidebar-accordions]",
    "[data-ff-collection-banner]",
    "[data-ff-collection-banner-grid]",
    "[data-collapsible-description]",
    "[data-ff-filter-modal]",
    "[data-ff-parallax-root]",
    "[data-ff-submit-on-change]",
    "[data-ff-select-on-click]",
    "[data-ff-print-trigger]",
    "[data-ff-row-link]",
    "[data-ff-scroll-story]",
    "recently-viewed-products",
    ".ff-blog-posts-list--presentation-parallax_page_cards",
    ".ff-informative-card-grid--presentation-parallax_page_cards",
    ".ff-feature-tiles-section--presentation-parallax_page_cards"
  ].join(",");

  const FF_PARALLAX_PAGE_CARDS_SELECTOR = [
    ".ff-blog-posts-list--presentation-parallax_page_cards",
    ".ff-informative-card-grid--presentation-parallax_page_cards",
    ".ff-feature-tiles-section--presentation-parallax_page_cards"
  ].join(",");

  function nodeMatchesOrContains(node, selector) {
    if (!node || node.nodeType !== 1) return false;
    if (node.matches && node.matches(selector)) return true;
    return !!(node.querySelector && node.querySelector(selector));
  }

  function collectInitRoot(node, bucket) {
    if (!node) return;

    if (node.nodeType === 1) {
      if (nodeMatchesOrContains(node, FF_INIT_SELECTOR)) bucket.add(node);
      return;
    }

    if (node.nodeType === 11 && node.childNodes) {
      node.childNodes.forEach((child) => collectInitRoot(child, bucket));
    }
  }

  function registerCleanup(node, fn) {
    if (!node || node.nodeType !== 1 || typeof fn !== "function") return;

    if (!node.__ffCleanups) node.__ffCleanups = [];
    node.__ffCleanups.push(fn);
    node.setAttribute("data-ff-cleanup-root", "true");
  }

  function runCleanup(node) {
    if (!node || node.nodeType !== 1) return;

    const targets = [];

    if (node.hasAttribute && node.hasAttribute("data-ff-cleanup-root")) {
      targets.push(node);
    }

    if (node.querySelectorAll) {
      node.querySelectorAll("[data-ff-cleanup-root]").forEach((target) => {
        targets.push(target);
      });
    }

    targets.forEach((target) => {
      const cleanups = target.__ffCleanups;
      if (!cleanups || !cleanups.length) return;

      cleanups.forEach((cleanup) => {
        try {
          cleanup();
        } catch (_) {}
      });

      delete target.__ffCleanups;
      target.removeAttribute("data-ff-cleanup-root");
    });
  }

  /* =============================
     PROSE TABLE SCROLLER (dynamic)
     - DOM-safe (no outerHTML)
  ============================== */
  function wrapProseTables(root) {
    qsa(root, ".prose table").forEach((table) => {
      const existingWrapper = table.closest(".table-scroller, .ff-table-scroll, .ff-table-wrap");

      if (existingWrapper) {
        if (table.classList.contains("ff-media-table")) {
          existingWrapper.classList.add("ff-table-scroll--media-table");
        }
        return;
      }

      const wrapper = document.createElement("div");
      wrapper.className = table.closest("[data-ff-main-article]") ? "ff-table-scroll" : "table-scroller";

      const parent = table.parentNode;
      if (!parent) return;

      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);

      if (table.classList.contains("ff-media-table")) {
        wrapper.classList.add("ff-table-scroll--media-table");
      }
    });
  }

  function markFfPresentationShells(root) {
    qsa(root, ".shopify-section").forEach((section) => {
      section.classList.toggle(
        "ff-section--parallax-page-cards",
        !!section.querySelector(FF_PARALLAX_PAGE_CARDS_SELECTOR)
      );
    });

    qsa(root, FF_PARALLAX_PAGE_CARDS_SELECTOR).forEach((sectionRoot) => {
      const shell = sectionRoot.closest(".shopify-section");
      if (shell) shell.classList.add("ff-section--parallax-page-cards");
    });
  }

  function initFfRecentlyViewedSections(root) {
    qsa(root, "recently-viewed-products").forEach((element) => {
      if (element.dataset.ffRecentlyViewedInit === "true") return;
      element.dataset.ffRecentlyViewedInit = "true";

      const shell = element.closest(".shopify-section--ff-recently-viewed-products");
      if (!shell) return;

      const sync = () => {
        shell.classList.toggle("ff-recently-viewed-products-empty", element.childElementCount === 0);
      };

      sync();

      if (!("MutationObserver" in window)) return;

      const observer = new MutationObserver(sync);
      observer.observe(element, { childList: true });

      registerCleanup(element, () => {
        observer.disconnect();
        delete element.dataset.ffRecentlyViewedInit;
      });
    });
  }

  /* =========================================
     PRODUCT CARD: DEFERRED SECONDARY IMAGE LOADER
     - works with ff-product-card defer_secondary output
     - loads on intent (hover/focus/touch)
     - prevents blank hover swap until secondary finishes loading
  ========================================== */
  function initFfDeferredSecondaryImages(root) {
  qsa(root, "product-card[data-ff-secondary-deferred]").forEach((card) => {
    if (card.dataset.ffSecondaryInit === "1") return;
    card.dataset.ffSecondaryInit = "1";

    const loadDeferredSecondary = () => {
      // Re-query every time (images can be replaced by swatches / rerenders)
      const secondary = card.querySelector(
        'img.product-card__image--secondary[data-src], img.product-card__image--secondary[data-srcset], img.product-card__image--secondary[data-sizes]'
      );

      // If there is no deferred secondary anymore, remove the "deferred" marker and bail
      if (!secondary) {
        card.removeAttribute("data-ff-secondary-deferred");
        return;
      }

      // Mark: this card uses deferred secondary (lets CSS block blank hover swap)
      card.setAttribute("data-ff-secondary-deferred", "true");

      // IMPORTANT: if the secondary image got replaced, "ready" must be reset
      card.removeAttribute("data-ff-secondary-ready");

      // Per-image guard (if the img node changes later, the new one can load)
      if (secondary.dataset.ffDeferredLoaded === "1") return;
      secondary.dataset.ffDeferredLoaded = "1";

      const primary = card.querySelector("img.product-card__image--primary");

      // Keep primary visible while secondary loads
      if (primary) primary.style.opacity = "1";
      secondary.style.opacity = "0";

      let settled = false;

      const finalize = () => {
        if (settled) return;
        settled = true;

        card.setAttribute("data-ff-secondary-ready", "true");
        card.removeAttribute("data-ff-secondary-deferred");

        if (primary) primary.style.removeProperty("opacity");
        secondary.style.removeProperty("opacity");

        // Remove data-* after load
        secondary.removeAttribute("data-src");
        secondary.removeAttribute("data-srcset");
        secondary.removeAttribute("data-sizes");
      };

      const fail = () => {
        // Keep primary visible, allow retry later
        if (settled) return;
        settled = true;

        if (primary) primary.style.removeProperty("opacity");
        secondary.style.removeProperty("opacity");

        delete secondary.dataset.ffDeferredLoaded;
        card.removeAttribute("data-ff-secondary-ready");
      };

      secondary.addEventListener("load", finalize, { once: true });
      secondary.addEventListener("error", fail, { once: true });

      // Start request
      const ds = secondary.dataset || {};
      try {
        secondary.decoding = "async";
        if ("fetchPriority" in secondary) secondary.fetchPriority = "low";
      } catch (_) {}

      secondary.removeAttribute("loading");

      if (ds.sizes) secondary.sizes = ds.sizes;
      if (ds.srcset) secondary.srcset = ds.srcset;
      if (ds.src) secondary.src = ds.src;

      // Cached image safety
      if (secondary.complete && secondary.naturalWidth > 0) {
        requestAnimationFrame(finalize);
      }
    };

    card.addEventListener("pointerenter", loadDeferredSecondary, { passive: true });
    card.addEventListener("focusin", loadDeferredSecondary);
    card.addEventListener("touchstart", loadDeferredSecondary, { passive: true });
    card.addEventListener("ff:load-secondary", loadDeferredSecondary);

    registerCleanup(card, () => {
      card.removeEventListener("pointerenter", loadDeferredSecondary);
      card.removeEventListener("focusin", loadDeferredSecondary);
      card.removeEventListener("touchstart", loadDeferredSecondary);
      card.removeEventListener("ff:load-secondary", loadDeferredSecondary);
      delete card.dataset.ffSecondaryInit;
    });
  });
}



  function initFfCollectionDescriptionCollapsibles(root) {
    qsa(root, "[data-collapsible-description]").forEach((wrap) => {
      if (wrap.dataset.collapseReady === "true") return;

      const button = wrap.querySelector("[data-collapsible-description-toggle]");
      const content = wrap.querySelector(".collection-description__inner");
      if (!button || !content) return;

      wrap.dataset.collapseReady = "true";

      const mq = window.matchMedia("(max-width: 999px)");
      let rafId = null;

      const setupMobile = () => {
        wrap.classList.add("is-collapsed");
        wrap.classList.remove("is-expanded");
        button.setAttribute("aria-expanded", "false");

        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          rafId = null;
          const hasOverflow = content.scrollHeight > content.clientHeight + 4;
          button.hidden = !hasOverflow;

          if (!hasOverflow) {
            wrap.classList.remove("is-collapsed", "is-expanded");
          }
        });
      };

      const setupDesktop = () => {
        if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }

        wrap.classList.remove("is-collapsed", "is-expanded");
        button.hidden = true;
        button.setAttribute("aria-expanded", "true");
      };

      const update = () => {
        if (mq.matches) setupMobile();
        else setupDesktop();
      };

      const onClick = () => {
        const expand = !wrap.classList.contains("is-expanded");
        wrap.classList.toggle("is-expanded", expand);
        wrap.classList.toggle("is-collapsed", !expand);
        button.setAttribute("aria-expanded", expand ? "true" : "false");
      };

      const onLoad = () => update();
      const onMqChange = () => update();

      button.addEventListener("click", onClick);
      window.addEventListener("load", onLoad, { once: true });

      if (mq.addEventListener) mq.addEventListener("change", onMqChange);
      else if (mq.addListener) mq.addListener(onMqChange);

      update();

      registerCleanup(wrap, () => {
        if (rafId) cancelAnimationFrame(rafId);
        button.removeEventListener("click", onClick);
        window.removeEventListener("load", onLoad);
        if (mq.removeEventListener) mq.removeEventListener("change", onMqChange);
        else if (mq.removeListener) mq.removeListener(onMqChange);
        delete wrap.dataset.collapseReady;
      });
    });
  }

  function initFfCollectionBanners(root) {
    qsa(root, "[data-ff-collection-banner]").forEach((banner) => {
      if (banner.dataset.ffCollectionBannerInit === "true") return;

      const text = banner.querySelector("[data-ff-collection-banner-text]");
      if (!text) return;

      banner.dataset.ffCollectionBannerInit = "true";

      const mq = window.matchMedia("(min-width: 1000px)");
      let rafId = null;
      let lastApplied = 0;
      let ro = null;
      let detached = false;

      const applySize = () => {
        rafId = null;
        if (detached) return;

        if (!mq.matches) {
          banner.style.removeProperty("--ff-collection-banner-square-size-desktop");
          lastApplied = 0;
          return;
        }

        const next = Math.ceil(text.getBoundingClientRect().height);
        if (!next) return;
        if (Math.abs(next - lastApplied) < 2) return;

        lastApplied = next;
        banner.style.setProperty("--ff-collection-banner-square-size-desktop", `${next}px`);
      };

      const queueApply = () => {
        if (detached) return;
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(applySize);
      };

      if ("ResizeObserver" in window) {
        ro = new ResizeObserver(queueApply);
        ro.observe(text);
      }

      window.addEventListener("resize", queueApply, { passive: true });

      const onMqChange = () => queueApply();
      if (mq.addEventListener) mq.addEventListener("change", onMqChange);
      else if (mq.addListener) mq.addListener(onMqChange);

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          if (!detached) queueApply();
        });
      }

      queueApply();

      registerCleanup(banner, () => {
        detached = true;
        if (rafId) cancelAnimationFrame(rafId);
        if (ro) ro.disconnect();
        window.removeEventListener("resize", queueApply);
        if (mq.removeEventListener) mq.removeEventListener("change", onMqChange);
        else if (mq.removeListener) mq.removeListener(onMqChange);
        banner.style.removeProperty("--ff-collection-banner-square-size-desktop");
        delete banner.dataset.ffCollectionBannerInit;
      });
    });
  }

  function initFfImageTextParallax(root) {
    qsa(root, "[data-ff-parallax-root]").forEach((outer) => {
      if (outer.dataset.ffParallaxInit === "true") return;
      outer.dataset.ffParallaxInit = "true";

      const enabled = outer.getAttribute("data-parallax") === "true";
      const prefersReducedMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const mobileMQ = window.matchMedia ? window.matchMedia("(max-width: 749px)") : null;
      const isMobile = mobileMQ ? mobileMQ.matches : false;

      if (!enabled || prefersReducedMotion || isMobile) return;

      const sectionEl = outer.querySelector(".ff-parallax__inner");
      const imageContainer = outer.querySelector("[data-parallax-image]");
      if (!sectionEl || !imageContainer) return;

      const speed = parseFloat(outer.getAttribute("data-parallax-speed")) || 0.5;
      let ticking = false;

      const handleScroll = () => {
        const rect = sectionEl.getBoundingClientRect();
        const winH = window.innerHeight || document.documentElement.clientHeight;
        if (rect.bottom <= 0 || rect.top >= winH) return;

        const scrolled = winH - rect.top;
        const total = winH + rect.height;
        const t = scrolled / total;
        const baseFactor = 0.3;
        const maxMove = rect.height * baseFactor;
        const intensity = 1.6;
        const offset = (t - 0.5) * maxMove * 2 * speed * intensity;

        imageContainer.style.transform = `translate3d(0,${offset.toFixed(2)}px,0)`;
      };

      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
      };

      const onMobileChange = (event) => {
        if (event.matches) {
          imageContainer.style.removeProperty("transform");
        } else {
          handleScroll();
        }
      };

      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onScroll, { passive: true });

      if (mobileMQ) {
        if (mobileMQ.addEventListener) mobileMQ.addEventListener("change", onMobileChange);
        else if (mobileMQ.addListener) mobileMQ.addListener(onMobileChange);
      }

      handleScroll();

      registerCleanup(outer, () => {
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onScroll);
        if (mobileMQ) {
          if (mobileMQ.removeEventListener) mobileMQ.removeEventListener("change", onMobileChange);
          else if (mobileMQ.removeListener) mobileMQ.removeListener(onMobileChange);
        }
        imageContainer.style.removeProperty("transform");
        delete outer.dataset.ffParallaxInit;
      });
    });
  }

  function initFfHeaderSidebarAccordions(root) {
    qsa(root, "[data-ff-header-sidebar-accordions]").forEach((sidebar) => {
      if (sidebar.dataset.ffHeaderSidebarAccordionsInit === "true") return;
      sidebar.dataset.ffHeaderSidebarAccordionsInit = "true";

      const detailsList = Array.from(sidebar.querySelectorAll(".header-sidebar__sub-panel details"));
      if (!detailsList.length) return;

      const onToggle = (event) => {
        const details = event.currentTarget;
        if (!details || !details.open) return;

        details
          .closest(".header-sidebar__sub-panel")
          ?.querySelectorAll("details[open]")
          .forEach((other) => {
            if (other !== details) other.removeAttribute("open");
          });
      };

      detailsList.forEach((details) => details.addEventListener("toggle", onToggle));

      registerCleanup(sidebar, () => {
        detailsList.forEach((details) => details.removeEventListener("toggle", onToggle));
        delete sidebar.dataset.ffHeaderSidebarAccordionsInit;
      });
    });
  }


  function initFfFilterModals(root) {
    qsa(root, "[data-ff-filter-modal]").forEach((component) => {
      if (component.dataset.ffFilterModalInit === "true") return;

      const trigger = component.querySelector("[data-filter-modal-trigger]");
      const dialog = component.querySelector("[data-filter-modal-dialog]");
      const overlay = component.querySelector("[data-filter-modal-overlay]");
      const closeButton = component.querySelector("[data-filter-modal-close]");

      if (!trigger || !dialog || !overlay || !closeButton) return;

      component.dataset.ffFilterModalInit = "true";

      const focusableSelector =
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
      let isOpen = false;
      let previouslyFocusedElement = null;

      const getFocusableElements = () => {
        return Array.from(dialog.querySelectorAll(focusableSelector)).filter(
          (el) => !el.hasAttribute("disabled") && el.getAttribute("tabindex") !== "-1"
        );
      };

      const focusFirstElement = () => {
        const focusables = getFocusableElements();
        if (focusables.length) {
          focusables[0].focus();
        } else {
          closeButton.focus();
        }
      };

      const trapFocus = (event) => {
        const focusables = getFocusableElements();
        if (!focusables.length) return;

        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      };

      const handleKeydown = (event) => {
        if (event.key === "Escape") {
          close();
          return;
        }

        if (event.key === "Tab") {
          trapFocus(event);
        }
      };

      const open = () => {
        if (isOpen) return;
        isOpen = true;

        previouslyFocusedElement = document.activeElement;

        dialog.classList.add("is-active");
        overlay.classList.add("is-active");
        dialog.setAttribute("aria-hidden", "false");
        trigger.setAttribute("aria-expanded", "true");
        document.documentElement.classList.add("lock");
        document.addEventListener("keydown", handleKeydown);

        focusFirstElement();
      };

      const close = () => {
        if (!isOpen) return;
        isOpen = false;

        dialog.classList.remove("is-active");
        overlay.classList.remove("is-active");
        dialog.setAttribute("aria-hidden", "true");
        trigger.setAttribute("aria-expanded", "false");
        document.documentElement.classList.remove("lock");
        document.removeEventListener("keydown", handleKeydown);

        if (previouslyFocusedElement && typeof previouslyFocusedElement.focus === "function") {
          previouslyFocusedElement.focus();
        } else {
          trigger.focus();
        }
      };

      const onTriggerClick = () => open();
      const onCloseClick = () => close();
      const onOverlayClick = () => close();

      trigger.addEventListener("click", onTriggerClick);
      closeButton.addEventListener("click", onCloseClick);
      overlay.addEventListener("click", onOverlayClick);

      registerCleanup(component, () => {
        trigger.removeEventListener("click", onTriggerClick);
        closeButton.removeEventListener("click", onCloseClick);
        overlay.removeEventListener("click", onOverlayClick);
        document.removeEventListener("keydown", handleKeydown);

        dialog.classList.remove("is-active");
        overlay.classList.remove("is-active");
        dialog.setAttribute("aria-hidden", "true");
        trigger.setAttribute("aria-expanded", "false");
        document.documentElement.classList.remove("lock");
        delete component.dataset.ffFilterModalInit;
      });
    });
  }
  function initFfInlineActionHelpers(root) {
    qsa(root, "[data-ff-submit-on-change]").forEach((field) => {
      if (field.dataset.ffSubmitOnChangeInit === "true") return;
      field.dataset.ffSubmitOnChangeInit = "true";

      const onChange = () => field.form && field.form.submit && field.form.submit();
      field.addEventListener("change", onChange);

      registerCleanup(field, () => {
        field.removeEventListener("change", onChange);
        delete field.dataset.ffSubmitOnChangeInit;
      });
    });

    qsa(root, "[data-ff-select-on-click]").forEach((field) => {
      if (field.dataset.ffSelectOnClickInit === "true") return;
      field.dataset.ffSelectOnClickInit = "true";

      const onClick = () => typeof field.select === "function" && field.select();
      field.addEventListener("click", onClick);

      registerCleanup(field, () => {
        field.removeEventListener("click", onClick);
        delete field.dataset.ffSelectOnClickInit;
      });
    });

    qsa(root, "[data-ff-print-trigger]").forEach((button) => {
      if (button.dataset.ffPrintTriggerInit === "true") return;
      button.dataset.ffPrintTriggerInit = "true";

      const onClick = () => window.print();
      button.addEventListener("click", onClick);

      registerCleanup(button, () => {
        button.removeEventListener("click", onClick);
        delete button.dataset.ffPrintTriggerInit;
      });
    });

    qsa(root, "[data-ff-row-link]").forEach((row) => {
      if (row.dataset.ffRowLinkInit === "true") return;

      const url = row.getAttribute("data-ff-row-link");
      if (!url) return;

      row.dataset.ffRowLinkInit = "true";

      const isInteractiveTarget = (target) => {
        return !!(target && target.closest && target.closest('a, button, input, select, textarea, summary, [role="button"], [role="link"]'));
      };

      const navigate = () => window.location.assign(url);

      const onClick = (event) => {
        if (isInteractiveTarget(event.target)) return;
        navigate();
      };

      const onKeydown = (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        if (isInteractiveTarget(event.target)) return;
        event.preventDefault();
        navigate();
      };

      row.addEventListener("click", onClick);
      row.addEventListener("keydown", onKeydown);

      registerCleanup(row, () => {
        row.removeEventListener("click", onClick);
        row.removeEventListener("keydown", onKeydown);
        delete row.dataset.ffRowLinkInit;
      });
    });
  }

  function initFfPageUtilities(root) {
    if (root && root !== document && root !== document.body && root !== document.documentElement) {
      return;
    }

    const pageType = window.themeVariables && window.themeVariables.settings
      ? window.themeVariables.settings.pageType
      : null;

    if (pageType === "policy") {
      qsa(document, ".shopify-policy__title").forEach((title) => title.classList.add("h2"));
      qsa(document, ".rte").forEach((rte) => rte.classList.add("prose"));
    }

    if (pageType === "") {
      qsa(document, ".shopify-email-marketing-confirmation__container").forEach((container) => {
        container.classList.add("prose");
      });
    }
  }

  /* ============= TABS ============= */

  function initFfTabs(root) {
    qsa(root, "[data-ff-tabs]").forEach(function (group) {
      if (group.dataset.ffTabsInit === "true") return;
      group.dataset.ffTabsInit = "true";
      setupTabs(group);
    });
  }

  function safeQueryById(container, id) {
    if (!id) return null;

    if (window.CSS && typeof CSS.escape === "function") {
      try {
        return container.querySelector("#" + CSS.escape(id));
      } catch (e) {}
    }
    return container.querySelector("#" + id.replace(/"/g, '\\"'));
  }

  function setupTabs(group) {
    var tablist = group.querySelector("[data-ff-tablist]") || group;
    var tabs = tablist.querySelectorAll("[data-ff-tab]");
    var panels = group.querySelectorAll("[data-ff-tab-panel]");
    if (!tabs.length || !panels.length) return;

    var cleanupFns = [];

    tablist.setAttribute("role", "tablist");

    tabs.forEach(function (tab, index) {
      var tabId = tab.id || nextId("ff-tab");
      tab.id = tabId;

      var targetId = tab.getAttribute("data-ff-tab-target");
      var panel = null;

      if (targetId) {
        panel = safeQueryById(group, targetId);
      } else if (panels[index]) {
        panel = panels[index];
        targetId = panel.id || nextId("ff-tab-panel");
      }

      if (panel) {
        panel.id = targetId;
        panel.setAttribute("role", "tabpanel");
        panel.setAttribute("aria-labelledby", tabId);
      }

      tab.setAttribute("role", "tab");
      tab.setAttribute("aria-controls", targetId || "");
      tab.setAttribute("tabindex", index === 0 ? "0" : "-1");
      tab.setAttribute("aria-selected", index === 0 ? "true" : "false");

      if (panel) panel.hidden = index !== 0;

      var onClick = function () {
        activateTab(group, tab);
      };

      var onKeydown = function (event) {
        handleTabKeydown(event, group);
      };

      tab.addEventListener("click", onClick);
      tab.addEventListener("keydown", onKeydown);

      cleanupFns.push(function () {
        tab.removeEventListener("click", onClick);
        tab.removeEventListener("keydown", onKeydown);
      });
    });

    registerCleanup(group, function () {
      cleanupFns.forEach(function (cleanup) {
        try {
          cleanup();
        } catch (_) {}
      });
      delete group.dataset.ffTabsInit;
    });
  }

  function activateTab(group, activeTab) {
    var tabs = group.querySelectorAll("[data-ff-tab]");
    var panels = group.querySelectorAll("[data-ff-tab-panel]");

    tabs.forEach(function (tab) {
      var isActive = tab === activeTab;
      tab.setAttribute("aria-selected", isActive ? "true" : "false");
      tab.setAttribute("tabindex", isActive ? "0" : "-1");
    });

    panels.forEach(function (panel) {
      var labelledBy = panel.getAttribute("aria-labelledby");
      var controllingTab = labelledBy ? group.querySelector("#" + labelledBy) : null;
      panel.hidden = controllingTab !== activeTab;
    });

    activeTab.focus();
  }

  function handleTabKeydown(event, group) {
    var key = event.key;
    if (
      key !== "ArrowLeft" &&
      key !== "ArrowRight" &&
      key !== "ArrowUp" &&
      key !== "ArrowDown" &&
      key !== "Home" &&
      key !== "End"
    ) {
      return;
    }

    var tabs = Array.prototype.slice.call(group.querySelectorAll("[data-ff-tab]"));
    var currentIndex = tabs.indexOf(document.activeElement);
    if (currentIndex === -1) return;

    event.preventDefault();

    var nextIndex = currentIndex;
    if (key === "ArrowLeft" || key === "ArrowUp") {
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (key === "ArrowRight" || key === "ArrowDown") {
      nextIndex = (currentIndex + 1) % tabs.length;
    } else if (key === "Home") {
      nextIndex = 0;
    } else if (key === "End") {
      nextIndex = tabs.length - 1;
    }

    var nextTab = tabs[nextIndex];
    if (nextTab) activateTab(group, nextTab);
  }

  /* ========== ACCORDIONS ========== */

  function initFfAccordions(root) {
    qsa(root, "[data-ff-accordion]").forEach(function (accordion) {
      if (accordion.dataset.ffAccordionInit === "true") return;
      accordion.dataset.ffAccordionInit = "true";
      setupAccordion(accordion);
    });
  }

  function setupAccordion(accordion) {
    var items = accordion.querySelectorAll("[data-ff-accordion-item]");
    if (!items.length) return;

    var cleanupFns = [];

    items.forEach(function (item) {
      var button = item.querySelector("[data-ff-accordion-button]");
      var panel = item.querySelector("[data-ff-accordion-panel]");
      if (!button || !panel) return;

      var btnId = button.id || nextId("ff-acc-btn");
      var panelId = panel.id || nextId("ff-acc-panel");
      button.id = btnId;
      panel.id = panelId;

      if (!button.getAttribute("type")) button.setAttribute("type", "button");

      button.setAttribute("aria-controls", panelId);

      var isOpen = !panel.hasAttribute("hidden");
      button.setAttribute("aria-expanded", isOpen ? "true" : "false");

      panel.setAttribute("role", "region");
      panel.setAttribute("aria-labelledby", btnId);
      panel.hidden = !isOpen;

      function handleToggle(ev) {
        if (ev.type === "keydown" && ev.key !== "Enter" && ev.key !== " ") return;
        ev.preventDefault();
        toggleAccordion(accordion, button, panel);
      }

      button.addEventListener("click", handleToggle);
      button.addEventListener("keydown", handleToggle);

      cleanupFns.push(function () {
        button.removeEventListener("click", handleToggle);
        button.removeEventListener("keydown", handleToggle);
      });
    });

    registerCleanup(accordion, function () {
      cleanupFns.forEach(function (cleanup) {
        try {
          cleanup();
        } catch (_) {}
      });
      delete accordion.dataset.ffAccordionInit;
    });
  }

  function toggleAccordion(accordion, button, panel) {
    var single = accordion.dataset.ffAccordionSingle === "true";
    var isOpen = button.getAttribute("aria-expanded") === "true";

    if (single) {
      accordion.querySelectorAll("[data-ff-accordion-button]").forEach(function (btn) {
        btn.setAttribute("aria-expanded", "false");
      });
      accordion.querySelectorAll("[data-ff-accordion-panel]").forEach(function (p) {
        p.hidden = true;
      });
    }

    button.setAttribute("aria-expanded", isOpen ? "false" : "true");
    panel.hidden = isOpen;
  }

  /* ========== HEADER ENHANCEMENTS (FF) ========== */

  function initFfHeader(root) {
    qsa(root, "[data-ff-header]").forEach((header) => {
      if (header.dataset.ffHeaderInit === "true") return;
      header.dataset.ffHeaderInit = "true";
      setupFfHeader(header);
    });
  }

  function closeDisclosure(detailsEl) {
    const wrapper = detailsEl.closest("mega-menu-disclosure, dropdown-menu-disclosure");
    if (wrapper && typeof wrapper.toggle === "function") {
      wrapper.toggle(false);
      return;
    }
    detailsEl.open = false;
    detailsEl.removeAttribute("open");
  }

  function setupFfHeader(header) {
    const disclosures = header.querySelectorAll("details[data-ff-menu-disclosure]");
    const cleanupFns = [];

    disclosures.forEach((d) => {
      const summary = d.querySelector(":scope > summary");

      const sync = () => {
        const panel = d.querySelector(".mega-menu, .header__dropdown-menu");
        if (!panel) return;

        const isOpen = !!d.open;

        panel.toggleAttribute("inert", !isOpen);
        panel.setAttribute("aria-hidden", isOpen ? "false" : "true");

        if (summary) summary.setAttribute("aria-expanded", isOpen ? "true" : "false");

        if (isOpen && summary && document.activeElement === summary) {
          const first = panel.querySelector('a,button,[tabindex]:not([tabindex="-1"])');
          if (first) first.focus({ preventScroll: true });
        }
      };

      sync();
      d.addEventListener("toggle", sync);
      cleanupFns.push(() => d.removeEventListener("toggle", sync));

      const onKeydown = (e) => {
        if (e.key !== "Escape") return;
        if (!d.open) return;
        e.preventDefault();
        closeDisclosure(d);
        summary && summary.focus({ preventScroll: true });
      };

      d.addEventListener("keydown", onKeydown);
      cleanupFns.push(() => d.removeEventListener("keydown", onKeydown));
    });

    header.querySelectorAll('details[data-ff-menu-disclosure] > summary').forEach((s) => {
      let done = false;
      const warm = () => {
        if (done) return;
        done = true;

        const d = s.parentElement;
        const panel = d && d.querySelector(".mega-menu, .header__dropdown-menu");
        if (!panel) return;

        panel.querySelectorAll('img[loading="lazy"]').forEach((img) => {
          img.removeAttribute("loading");
          try {
            img.decoding = "async";
            if ("fetchPriority" in img) img.fetchPriority = "low";
          } catch (_) {}
        });
      };

      s.addEventListener("pointerenter", warm, { once: true, passive: true });
      s.addEventListener("focus", warm, { once: true });
      s.addEventListener("touchstart", warm, { once: true, passive: true });

      cleanupFns.push(() => {
        s.removeEventListener("pointerenter", warm);
        s.removeEventListener("focus", warm);
        s.removeEventListener("touchstart", warm);
      });
    });

    registerCleanup(header, () => {
      cleanupFns.forEach((cleanup) => {
        try {
          cleanup();
        } catch (_) {}
      });
      delete header.dataset.ffHeaderInit;
    });
  }

  /* ========== ANNOUNCEMENT BAR (FF) ========== */

  function initFfAnnouncementBars(root) {
    qsa(root, "[data-ff-announce]").forEach((bar) => {
      if (bar.dataset.ffAnnounceInit === "true") return;
      bar.dataset.ffAnnounceInit = "true";
      setupFfAnnouncementBar(bar);
    });
  }

  function setupFfAnnouncementBar(root) {
    const designMode = !!(window.Shopify && Shopify.designMode);
    const storageBase = root.getAttribute("data-announcement-storage-key") || root.id || nextId("ff-announce");
    const storageKey = `ff-announce-${storageBase}-closed`;

    const safeSessionGet = () => {
      try {
        return sessionStorage.getItem(storageKey);
      } catch (_) {
        return null;
      }
    };

    const safeSessionSet = () => {
      try {
        sessionStorage.setItem(storageKey, "true");
      } catch (_) {}
    };

    if (!designMode && safeSessionGet() === "true") {
      root.setAttribute("data-hidden", "true");
      document.documentElement.style.setProperty("--announcement-bar-height", "0px");
      return;
    }

    const list = root.querySelector(".ff-announce__messages");
    if (!list) {
      delete root.dataset.ffAnnounceInit;
      return;
    }

    const btn = root.querySelector(".ff-announce__close");

    const ac = "AbortController" in window ? new AbortController() : null;
    const cleanupFns = [];
    const bind = (target, eventName, handler, options) => {
      target.addEventListener(eventName, handler, options);
      if (!ac) cleanupFns.push(() => target.removeEventListener(eventName, handler, options));
    };
    const listenerOptions = ac ? { signal: ac.signal } : undefined;
    const passiveListenerOptions = ac ? { passive: true, signal: ac.signal } : { passive: true };

    let items = Array.prototype.slice.call(list.querySelectorAll(".ff-announce__message"));

    function updateAnnouncementHeight() {
      if (root.getAttribute("data-hidden") === "true") {
        document.documentElement.style.setProperty("--announcement-bar-height", "0px");
        return;
      }

      const inner = root.querySelector(".ff-announce__inner");
      if (!inner) return;

      const height = inner.getBoundingClientRect().height || 0;
      document.documentElement.style.setProperty("--announcement-bar-height", `${height}px`);
    }

    const country = (root.getAttribute("data-country") || "EU").toUpperCase();

    function regionOK(region, code) {
      if (region === "all") return true;
      if (region === "uk") return code === "GB" || code === "UK";
      if (region === "ch") return code === "CH";

      const eu = [
        "DE", "FR", "IT", "ES", "NL", "BE", "AT", "PL", "SE", "DK", "FI", "NO", "IE",
        "PT", "GR", "CZ", "RO", "HU", "LU", "EE", "LV", "LT", "SI", "SK", "HR", "BG",
        "CY", "MT"
      ];

      return eu.indexOf(code) > -1;
    }

    items.forEach((item) => {
      const region = (item.getAttribute("data-region") || "all").toLowerCase();
      if (!regionOK(region, country)) {
        item.setAttribute("data-visible", "false");
      }
    });

    items = items.filter((item) => item.getAttribute("data-visible") === "true");

    if (!items.length) {
      root.setAttribute("data-hidden", "true");
      document.documentElement.style.setProperty("--announcement-bar-height", "0px");
      return;
    }

    const mq = window.matchMedia("(max-width:749px)");
    let isMobile = mq.matches;
    let index = 0;
    let timer = null;
    const intervalSeconds = parseInt(root.getAttribute("data-rotation-interval"), 10) || 8;
    const reducedMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion:reduce)").matches;
    let removeMqListener = null;
    let startX = 0;
    let endX = 0;

    const onTouchStart = (event) => {
      startX = event.changedTouches[0].screenX;
    };

    const onTouchEnd = (event) => {
      endX = event.changedTouches[0].screenX;
      const delta = startX - endX;
      if (Math.abs(delta) > 50) {
        delta > 0 ? next() : prev();
      }
    };

    function setActive(nextIndex) {
      items.forEach((item, itemIndex) => {
        item.setAttribute("data-active", itemIndex === nextIndex ? "true" : "false");
      });
      updateAnnouncementHeight();
    }

    function next() {
      index = (index + 1) % items.length;
      setActive(index);
    }

    function prev() {
      index = (index - 1 + items.length) % items.length;
      setActive(index);
    }

    function start() {
      if (timer) clearInterval(timer);
      timer = setInterval(next, intervalSeconds * 1000);
    }

    function stop() {
      if (!timer) return;
      clearInterval(timer);
      timer = null;
    }

    function init() {
      stop();

      if (isMobile) {
        index = 0;
        setActive(0);

        if (items.length > 1 && !reducedMotion) {
          start();
        }

        if (!list._ffSwipeBound) {
          list._ffSwipeBound = true;

          bind(list, "touchstart", onTouchStart, passiveListenerOptions);
          bind(list, "touchend", onTouchEnd, passiveListenerOptions);
        }
      } else {
        items.forEach((item) => item.removeAttribute("data-active"));
        updateAnnouncementHeight();
      }
    }

    init();
    updateAnnouncementHeight();

    const onMqChange = (event) => {
      isMobile = event.matches;
      init();
    };

    if (mq.addEventListener) {
      mq.addEventListener("change", onMqChange);
      removeMqListener = () => mq.removeEventListener("change", onMqChange);
    } else if (mq.addListener) {
      mq.addListener(onMqChange);
      removeMqListener = () => mq.removeListener(onMqChange);
    }

    bind(window, "resize", updateAnnouncementHeight, passiveListenerOptions);

    if (btn) {
      const onClose = () => {
        if (!designMode) {
          safeSessionSet();
        }

        root.setAttribute("data-hidden", "true");
        stop();
        document.documentElement.style.setProperty("--announcement-bar-height", "0px");
      };

      bind(btn, "click", onClose, listenerOptions);
    }

    registerCleanup(root, () => {
      stop();
      if (removeMqListener) removeMqListener();
      if (ac) ac.abort();
      cleanupFns.forEach((cleanup) => {
        try {
          cleanup();
        } catch (_) {}
      });
      delete list._ffSwipeBound;
      delete root.dataset.ffAnnounceInit;
    });
  }

  function initFfMainProduct(root) {
    qsa(root, "[data-ff-main-product]").forEach((section) => {
      if (section.dataset.ffMainProductInit === "1") return;
      section.dataset.ffMainProductInit = "1";

      const productId = section.dataset.ffProductId;
      if (!productId) return;

      try {
        let recentlyViewedProducts = JSON.parse(localStorage.getItem("theme:recently-viewed-products") || "[]");
        recentlyViewedProducts = recentlyViewedProducts.filter((item) => String(item) !== String(productId));
        recentlyViewedProducts.unshift(Number(productId));
        localStorage.setItem("theme:recently-viewed-products", JSON.stringify(recentlyViewedProducts));
      } catch (_) {}

      const tailBlockTypes = ["ff-blog-teaser", "image", "modal"];
const tailSelector = [
  ...tailBlockTypes.map((type) => `.product-info__block-item[data-block-type="${type}"]`),
  `.product-info__block-item[data-ff-mobile-below-gallery="true"]`
].join(",");
      const mqDesktop = window.matchMedia("(min-width: 1000px)");
      let rafId = 0;
      let mutationObserver = null;
      let removeMqListener = null;

      function ensureTrackedTailItems() {
        if (!tailSelector) return;

        qsa(section, tailSelector).forEach((item) => {
          if (item.dataset.ffMobileTailId) return;
          if (!item.parentNode) return;

          const tailId = nextId("ff-mobile-tail");
          const placeholder = document.createElement("div");

          placeholder.hidden = true;
          placeholder.setAttribute("data-ff-mobile-tail-placeholder", tailId);
          item.dataset.ffMobileTailId = tailId;

          item.parentNode.insertBefore(placeholder, item);
        });
      }

      function getTailPlaceholderMap() {
        return Array.from(section.querySelectorAll("[data-ff-mobile-tail-placeholder]")).map((placeholder) => {
          const tailId = placeholder.getAttribute("data-ff-mobile-tail-placeholder");
          const item = tailId
            ? section.querySelector(`.product-info__block-item[data-ff-mobile-tail-id="${tailId}"]`)
            : null;

          return { placeholder, item };
        });
      }

      function pruneOrphanPlaceholders() {
        getTailPlaceholderMap().forEach(({ placeholder, item }) => {
          if (!item && placeholder.parentNode) placeholder.parentNode.removeChild(placeholder);
        });
      }

      function restoreDesktopTailItems() {
        getTailPlaceholderMap().forEach(({ placeholder, item }) => {
          if (!placeholder || !item || !placeholder.parentNode) return;

          if (item.parentNode !== placeholder.parentNode || placeholder.nextSibling !== item) {
            placeholder.parentNode.insertBefore(item, placeholder.nextSibling);
          }
        });
      }

      function moveTailItemsBelowGalleryOnMobile() {
  const belowGallery = section.querySelector("#product-extra-information");
  if (!belowGallery) return;

  getTailPlaceholderMap().forEach(({ item }) => {
    if (!item) return;

    const targetId = item.getAttribute("data-ff-mobile-below-gallery-target-id");
    let target = null;

    if (targetId) {
      try {
        target = section.querySelector(
          `[data-ff-mobile-below-gallery-target="${CSS.escape(targetId)}"]`
        );
      } catch (_) {
        target = section.querySelector(
          `[data-ff-mobile-below-gallery-target="${targetId.replace(/"/g, '\\"')}"]`
        );
      }
    }

    if (target && target.parentNode) {
      if (target.nextSibling !== item) {
        target.parentNode.insertBefore(item, target.nextSibling);
      }
      return;
    }

    if (item.parentNode !== belowGallery) {
      belowGallery.appendChild(item);
    }
  });
}

      function applyMobileTailLayout() {
        ensureTrackedTailItems();
        pruneOrphanPlaceholders();

        if (mqDesktop.matches) restoreDesktopTailItems();
        else moveTailItemsBelowGalleryOnMobile();
      }

      function scheduleMobileTailLayout() {
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          rafId = 0;
          applyMobileTailLayout();
        });
      }

      scheduleMobileTailLayout();

      const onMqChange = () => scheduleMobileTailLayout();

      if (mqDesktop.addEventListener) {
        mqDesktop.addEventListener("change", onMqChange);
        removeMqListener = () => mqDesktop.removeEventListener("change", onMqChange);
      } else if (mqDesktop.addListener) {
        mqDesktop.addListener(onMqChange);
        removeMqListener = () => mqDesktop.removeListener(onMqChange);
      }

      if ("MutationObserver" in window) {
        mutationObserver = new MutationObserver(scheduleMobileTailLayout);
        mutationObserver.observe(section, { childList: true, subtree: true });
      }

      registerCleanup(section, () => {
        if (rafId) cancelAnimationFrame(rafId);
        if (mutationObserver) mutationObserver.disconnect();
        if (removeMqListener) removeMqListener();
        restoreDesktopTailItems();

        getTailPlaceholderMap().forEach(({ placeholder, item }) => {
          if (item) item.removeAttribute("data-ff-mobile-tail-id");
          if (placeholder && placeholder.parentNode) {
            placeholder.parentNode.removeChild(placeholder);
          }
        });

        delete section.dataset.ffMainProductInit;
      });
    });
  }


  /* ========== BACK TO TOP (FF) ========== */

  function initFfBackToTop(root) {
    qsa(root, "[data-ff-backtotop]").forEach((btn) => {
      if (btn.dataset.ffBacktotopInit === "true") return;
      btn.dataset.ffBacktotopInit = "true";

      const mq =
        "matchMedia" in window
          ? window.matchMedia("(prefers-reduced-motion: reduce)")
          : null;

      const setHidden = (hidden) => {
        btn.classList.toggle("show", !hidden);
        btn.setAttribute("aria-hidden", hidden ? "true" : "false");
        if (hidden) btn.setAttribute("tabindex", "-1");
        else btn.removeAttribute("tabindex");
      };

      if (!document.body) return;

      let sentinel = document.getElementById("ff-top-sentinel");
      if (!sentinel) {
        sentinel = document.createElement("div");
        sentinel.id = "ff-top-sentinel";
        sentinel.setAttribute("aria-hidden", "true");
        sentinel.style.cssText =
          "position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none;";
        document.body.insertBefore(sentinel, document.body.firstChild);
      }

      const ac = "AbortController" in window ? new AbortController() : null;
      const listenerOptions = ac ? { signal: ac.signal } : undefined;
      const passiveListenerOptions = ac ? { passive: true, signal: ac.signal } : { passive: true };
      let io = null;
      let onScroll = null;

      setHidden(true);

      if ("IntersectionObserver" in window) {
        io = new IntersectionObserver((entries) => {
          setHidden(entries[0].isIntersecting);
        });
        io.observe(sentinel);
      } else {
        let ticking = false;
        onScroll = () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            setHidden(window.scrollY <= 400);
            ticking = false;
          });
        };
        window.addEventListener("scroll", onScroll, passiveListenerOptions);
        onScroll();
      }

      const onClick = () => {
        if (mq && mq.matches) {
          window.scrollTo(0, 0);
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      };

      btn.addEventListener("click", onClick, listenerOptions);

      registerCleanup(btn, () => {
        if (io) io.disconnect();
        if (!ac && onScroll) window.removeEventListener("scroll", onScroll);
        if (!ac) btn.removeEventListener("click", onClick);
        if (ac) ac.abort();
        setHidden(true);
        delete btn.dataset.ffBacktotopInit;
      });
    });
  }

  /* ========== LIGHT PARALLAX TABLE WRAP (FF) ========== */

  function initFfLightParallax(root) {
    qsa(root, "[data-ff-light-parallax]").forEach((section) => {
      if (section.dataset.ffLightParallaxInit === "true") return;
      section.dataset.ffLightParallaxInit = "true";

      const wrappers = [];

      section.querySelectorAll("table.ff-table-shipping").forEach((table) => {
        const parent = table.parentElement;
        if (parent && parent.classList && parent.classList.contains("ff-table-scroll")) return;

        const wrapper = document.createElement("div");
        wrapper.className = "ff-table-scroll";

        const tableParent = table.parentNode;
        if (!tableParent) return;

        tableParent.insertBefore(wrapper, table);
        wrapper.appendChild(table);
        wrappers.push(wrapper);
      });

      registerCleanup(section, () => {
        wrappers.forEach((wrapper) => {
          const parent = wrapper.parentNode;
          if (!parent) return;

          while (wrapper.firstChild) {
            parent.insertBefore(wrapper.firstChild, wrapper);
          }

          parent.removeChild(wrapper);
        });

        delete section.dataset.ffLightParallaxInit;
      });
    });
  }

  /* ========== QUICK ACCESS (FF) ========== */

  function initFfNewsletterPopupSuccess(root) {
    qsa(root, "[data-ff-newsletter-popup-success]").forEach((marker) => {
      if (marker.dataset.ffNewsletterPopupSuccessInit === "1") return;
      marker.dataset.ffNewsletterPopupSuccessInit = "1";

      try {
        localStorage.setItem("theme:popup-filled", "true");
      } catch (_) {}
    });
  }

  function initFfQuickAccess(root) {
    qsa(root, "[data-ff-quick-access]").forEach((nav) => {
      if (nav.dataset.ffQuickAccessInit === "true") return;
      nav.dataset.ffQuickAccessInit = "true";
      setupFfQuickAccess(nav);
    });
  }

  function setupFfQuickAccess(root) {
    const bar = root.querySelector(".bar");
    const links = Array.prototype.slice.call(root.querySelectorAll("[data-ffqa-link]"));
    if (!bar || !links.length) return;

    const ac = "AbortController" in window ? new AbortController() : null;
    const listenerOptions = ac ? { signal: ac.signal } : undefined;
    const passiveListenerOptions = ac ? { passive: true, signal: ac.signal } : { passive: true };

    let spacer = root.nextElementSibling;
    if (!spacer || !spacer.classList.contains("ffqa-spacer")) spacer = null;

    const targets = [];
    let io = null;
    let resizeObserver = null;
    let mutationObserver = null;
    let rafId = 0;

    function getFooterGroup() {
      return (
        document.querySelector(".shopify-section-group-footer-group") ||
        document.getElementById("ff-footer") ||
        document.querySelector("footer")
      );
    }

    function resolveTarget(rawId) {
      if (!rawId) return null;

      let el = document.getElementById(rawId);
      if (el) return el;

      const isShopifyPref = rawId.indexOf("shopify-section-") === 0;
      const key = isShopifyPref ? rawId.replace(/^shopify-section-/, "") : rawId;
      const wrappers = document.querySelectorAll('[id^="shopify-section-"]');

      for (let i = 0; i < wrappers.length; i += 1) {
        const cid = wrappers[i].id;
        if (cid === rawId) return wrappers[i];
        if (cid.slice(-(key.length + 2)) === "__" + key) return wrappers[i];
        if (!isShopifyPref && cid.slice(-(rawId.length + 2)) === "__" + rawId) return wrappers[i];
      }

      return null;
    }

    links.forEach((link) => {
      const id = link.getAttribute("data-ffqa-link");
      if (!id) return;

      const target = resolveTarget(id);
      if (!target) return;

      target.setAttribute("data-ffqa-target", "");
      targets.push(target);

      if (target.id && link.getAttribute("href") !== "#" + target.id) {
        link.setAttribute("href", "#" + target.id);
      }
    });

    function setOffset() {
      const selectors = [
        ".shopify-section--announcement-bar",
        ".shopify-section--announcement-bar .announcement-bar",
        ".shopify-section--header",
        ".shopify-section--header .header"
      ];

      let maxBottom = 0;

      for (let i = 0; i < selectors.length; i += 1) {
        const el = document.querySelector(selectors[i]);
        if (!el) continue;

        const cs = getComputedStyle(el);
        if (cs.display === "none" || cs.visibility === "hidden" || cs.opacity === "0") continue;

        const rect = el.getBoundingClientRect();
        if (rect.bottom > maxBottom) maxBottom = rect.bottom;
      }

      if (maxBottom <= 0) {
        const fallback = parseFloat(getComputedStyle(root).getPropertyValue("--qa-offset")) || 0;
        maxBottom = fallback;
      }

      const rounded = Math.round(maxBottom);
      root.style.setProperty("--qa-offset-dyn", rounded + "px");

      if (targets.length) {
        const offsetWithGap = rounded + 12;
        targets.forEach((target) => {
          target.style.scrollMarginTop = offsetWithGap + "px";
        });
      }
    }

    function setBarHeight() {
      if (!spacer) return;

      const isSidebarDesktop =
        window.matchMedia("(min-width:1000px)").matches &&
        root.getAttribute("data-desktop-position") === "sidebar";

      if (isSidebarDesktop) {
        spacer.style.height = "0px";
        return;
      }

      const height = bar.getBoundingClientRect().height || 0;
      spacer.style.height = height + "px";
    }

    function clampToFooter() {
      const footerGroup = getFooterGroup();
      if (!footerGroup) return;

      const rect = footerGroup.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      root.classList.toggle("ffqa--hide-footer", rect.top <= vh);
    }

    function scheduleUpdate() {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        setOffset();
        setBarHeight();
        clampToFooter();
      });
    }

    function bindIO() {
      if (!("IntersectionObserver" in window) || !targets.length) return;

      io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const id = entry.target.id;
          links.forEach((link) => {
            link.classList.toggle(
              "is-active",
              entry.isIntersecting && link.getAttribute("href") === "#" + id
            );
          });
        });
      }, {
        rootMargin: "-45% 0px -50% 0px",
        threshold: 0.01
      });

      targets.forEach((target) => io.observe(target));
    }

    function jumpToHash() {
      if (location.hash.length <= 1) return;

      const raw = location.hash.slice(1);
      const target = resolveTarget(raw);
      if (!target) return;

      setTimeout(() => {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 30);
    }

    scheduleUpdate();
    bindIO();
    jumpToHash();

    const onHashChange = () => {
      jumpToHash();
      scheduleUpdate();
    };

    window.addEventListener("hashchange", onHashChange, listenerOptions);
    window.addEventListener("resize", scheduleUpdate, passiveListenerOptions);
    window.addEventListener("scroll", scheduleUpdate, passiveListenerOptions);

    const observedChrome = [
      document.querySelector(".shopify-section--header"),
      document.querySelector(".shopify-section--announcement-bar")
    ].filter(Boolean);

    if ("ResizeObserver" in window && observedChrome.length) {
      resizeObserver = new ResizeObserver(scheduleUpdate);
      observedChrome.forEach((target) => resizeObserver.observe(target));
    } else if ("MutationObserver" in window && observedChrome.length) {
      mutationObserver = new MutationObserver(scheduleUpdate);
      observedChrome.forEach((target) => {
        mutationObserver.observe(target, { attributes: true, attributeFilter: ["class", "style"] });
      });
    }

    registerCleanup(root, () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (io) io.disconnect();
      if (resizeObserver) resizeObserver.disconnect();
      if (mutationObserver) mutationObserver.disconnect();
      if (ac) ac.abort();
      root.style.removeProperty("--qa-offset-dyn");
      root.classList.remove("ffqa--hide-footer");
      if (spacer) spacer.style.removeProperty("height");
      targets.forEach((target) => {
        target.removeAttribute("data-ffqa-target");
        target.style.removeProperty("scroll-margin-top");
      });
      delete root.dataset.ffQuickAccessInit;
    });
  }


  /* ========== REINTRO PASS 7 — interactive content bundle ========== */

  /* ========== TABS PARALLAX ========== */

  function initFfTabsParallax(root) {
    qsa(root, "[data-ff-tabs-parallax]").forEach((sectionEl) => {
      if (sectionEl.dataset.ffTabsParallaxInit === "true") return;
      sectionEl.dataset.ffTabsParallaxInit = "true";
      setupFfTabsParallax(sectionEl);
    });
  }

  function setupFfTabsParallax(sectionEl) {
    const desktopEl = sectionEl.querySelector(".ff-tabs-parallax__desktop");
    const tabViewport = desktopEl ? desktopEl.querySelector("[data-ff-tab-viewport]") : null;
    const prevButton = desktopEl ? desktopEl.querySelector(".ff-tabs-parallax__scroll-control.is-prev") : null;
    const nextButton = desktopEl ? desktopEl.querySelector(".ff-tabs-parallax__scroll-control.is-next") : null;
    const tabButtons = desktopEl ? Array.from(desktopEl.querySelectorAll(".ff-tabs-parallax__tab-button")) : [];
    const tabPanels = desktopEl ? Array.from(desktopEl.querySelectorAll(".ff-tabs-parallax__desktop-panel")) : [];

    const ac = "AbortController" in window ? new AbortController() : null;
    const cleanupFns = [];
    const bind = (target, eventName, handler, options) => {
      target.addEventListener(eventName, handler, options);
      if (!ac) cleanupFns.push(() => target.removeEventListener(eventName, handler, options));
    };

    const listenerOptions = ac ? { signal: ac.signal } : undefined;
    const passiveListenerOptions = ac ? { passive: true, signal: ac.signal } : { passive: true };
    const wheelOptions = ac ? { passive: false, signal: ac.signal } : { passive: false };
    const mqDesktop = window.matchMedia("(min-width: 1000px)");
    let removeMqListener = null;
    let rafId = 0;

    const isDesktop = () => mqDesktop.matches;
    const isScrollLayout = () => desktopEl && desktopEl.classList.contains("ff-tabs-parallax__desktop--scroll");

    const updateScrollState = () => {
      rafId = 0;

      if (!desktopEl || !tabViewport || !isScrollLayout() || !isDesktop()) {
        if (desktopEl) desktopEl.classList.remove("is-overflowing", "is-start", "is-end");
        return;
      }

      const overflow = tabViewport.scrollWidth > tabViewport.clientWidth + 2;
      const atStart = tabViewport.scrollLeft <= 2;
      const atEnd = tabViewport.scrollLeft + tabViewport.clientWidth >= tabViewport.scrollWidth - 2;

      desktopEl.classList.toggle("is-overflowing", overflow);
      desktopEl.classList.toggle("is-start", atStart);
      desktopEl.classList.toggle("is-end", atEnd);
    };

    const queueUpdateScrollState = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateScrollState);
    };

    const activateTab = (index, options = {}) => {
      const settings = { focus: false, scroll: false, ...options };
      if (!tabButtons.length || !tabPanels.length) return;
      if (index < 0 || index >= tabButtons.length) return;

      tabButtons.forEach((button, buttonIndex) => {
        const isActive = buttonIndex === index;
        button.setAttribute("aria-selected", isActive ? "true" : "false");
        button.tabIndex = isActive ? 0 : -1;
      });

      tabPanels.forEach((panel, panelIndex) => {
        panel.hidden = panelIndex !== index;
      });

      const activeButton = tabButtons[index];
      if (!activeButton) return;
      if (settings.focus) activeButton.focus();

      if (settings.scroll && tabViewport && isScrollLayout() && isDesktop()) {
        activeButton.scrollIntoView({ behavior: "smooth", inline: "nearest", block: "nearest" });
      }

      queueUpdateScrollState();
    };

    if (tabButtons.length && tabPanels.length) {
      tabButtons.forEach((button, index) => {
        bind(button, "click", () => {
          activateTab(index, { scroll: true });
        }, listenerOptions);

        bind(button, "keydown", (event) => {
          let targetIndex = null;

          switch (event.key) {
            case "ArrowRight":
            case "ArrowDown":
              targetIndex = (index + 1) % tabButtons.length;
              break;
            case "ArrowLeft":
            case "ArrowUp":
              targetIndex = (index - 1 + tabButtons.length) % tabButtons.length;
              break;
            case "Home":
              targetIndex = 0;
              break;
            case "End":
              targetIndex = tabButtons.length - 1;
              break;
          }

          if (targetIndex === null) return;
          event.preventDefault();
          activateTab(targetIndex, { focus: true, scroll: true });
        }, listenerOptions);
      });
    }

    if (prevButton && tabViewport) {
      bind(prevButton, "click", () => {
        tabViewport.scrollBy({
          left: -Math.max(220, Math.round(tabViewport.clientWidth * 0.45)),
          behavior: "smooth"
        });
      }, listenerOptions);
    }

    if (nextButton && tabViewport) {
      bind(nextButton, "click", () => {
        tabViewport.scrollBy({
          left: Math.max(220, Math.round(tabViewport.clientWidth * 0.45)),
          behavior: "smooth"
        });
      }, listenerOptions);
    }

    if (tabViewport) {
      bind(tabViewport, "scroll", queueUpdateScrollState, passiveListenerOptions);
      bind(tabViewport, "wheel", (event) => {
        if (!isDesktop() || !isScrollLayout()) return;

        const overflow = tabViewport.scrollWidth > tabViewport.clientWidth + 2;
        if (!overflow) return;
        if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

        event.preventDefault();
        tabViewport.scrollLeft += event.deltaY;
      }, wheelOptions);
    }

    bind(window, "resize", queueUpdateScrollState, passiveListenerOptions);
    bind(window, "load", queueUpdateScrollState, listenerOptions);

    const onMqChange = () => queueUpdateScrollState();
    if (mqDesktop.addEventListener) {
      mqDesktop.addEventListener("change", onMqChange);
      removeMqListener = () => mqDesktop.removeEventListener("change", onMqChange);
    } else if (mqDesktop.addListener) {
      mqDesktop.addListener(onMqChange);
      removeMqListener = () => mqDesktop.removeListener(onMqChange);
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (!sectionEl.isConnected) return;
        queueUpdateScrollState();
      });
    }

    if (window.Shopify && Shopify.designMode) {
      const onBlockSelect = (event) => {
        const blockEl = event.target.closest("[data-ff-panel-index], [data-ff-accordion-index]");
        if (!blockEl || !sectionEl.contains(blockEl)) return;

        const rawIndex = blockEl.getAttribute("data-ff-panel-index") || blockEl.getAttribute("data-ff-accordion-index");
        const index = Number(rawIndex);
        if (Number.isNaN(index)) return;

        if (isDesktop()) {
          activateTab(index, { scroll: true });
        } else {
          const accordionItem = sectionEl.querySelector('[data-ff-accordion-index="' + index + '"]');
          if (accordionItem) accordionItem.open = true;
        }
      };

      bind(document, "shopify:block:select", onBlockSelect, listenerOptions);
    }

    queueUpdateScrollState();

    registerCleanup(sectionEl, () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (removeMqListener) removeMqListener();
      if (ac) ac.abort();
      cleanupFns.forEach((cleanup) => {
        try {
          cleanup();
        } catch (_) {}
      });
      delete sectionEl.dataset.ffTabsParallaxInit;
    });
  }

  /* ========== PARALLAX MULTI-COLUMN (built-in bg mode) ========== */

  function initFfParallaxMultiColumn(root) {
    qsa(root, "[data-ff-parallax-multi-column]").forEach((sectionEl) => {
      if (sectionEl.dataset.ffParallaxMultiColumnInit === "true") return;
      sectionEl.dataset.ffParallaxMultiColumnInit = "true";
      setupFfParallaxMultiColumn(sectionEl);
    });
  }

  function setupFfParallaxMultiColumn(root) {
    if (!root.classList.contains("ff-parallax-multi-column--builtin-bg")) return;
    if (root.getAttribute("data-parallax") !== "on") return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const layer = root.querySelector(".ffpmc-bg-layer");
    if (!layer) return;

    const ac = "AbortController" in window ? new AbortController() : null;
    const cleanupFns = [];
    const bind = (target, eventName, handler, options) => {
      target.addEventListener(eventName, handler, options);
      if (!ac) cleanupFns.push(() => target.removeEventListener(eventName, handler, options));
    };

    const passiveListenerOptions = ac ? { passive: true, signal: ac.signal } : { passive: true };
    let rafId = 0;

    const strengthToken = parseFloat(root.getAttribute("data-parallax-strength") || "15");
    const maxShift = Math.max(0, Math.min(strengthToken, 40)) * 6;

    const update = () => {
      rafId = 0;

      if (window.innerWidth < 700) {
        layer.style.transform = "translate3d(0,0,0)";
        return;
      }

      const rect = root.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const mid = rect.top + rect.height * 0.5;
      const t = (mid - vh * 0.5) / vh;
      const shift = Math.max(-maxShift, Math.min(maxShift, -t * maxShift));

      layer.style.transform = "translate3d(0," + shift.toFixed(2) + "px,0)";
    };

    const queueUpdate = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(update);
    };

    queueUpdate();
    bind(window, "scroll", queueUpdate, passiveListenerOptions);
    bind(window, "resize", queueUpdate, passiveListenerOptions);

    registerCleanup(root, () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (ac) ac.abort();
      cleanupFns.forEach((cleanup) => {
        try {
          cleanup();
        } catch (_) {}
      });
      layer.style.removeProperty("transform");
      delete root.dataset.ffParallaxMultiColumnInit;
    });
  }

  function initFfCareBlog(root) {
    qsa(root, "[data-ff-care-blog]").forEach((section) => {
      if (section.dataset.ffCareBlogInit === "1") return;
      section.dataset.ffCareBlogInit = "1";

      const cleanups = [];
      const ownerToken = nextId("ff-care-blog");
      const body = document.body;

      if (body) {
        body.dataset.ffCareBlogOwner = ownerToken;
        body.dataset.ffCareBlogBg = section.dataset.ffCareBlogBg || "0";

        const bgUrl = section.dataset.ffCareBlogBgUrl || "";
        if (bgUrl) {
          const safeUrl = bgUrl.replace(/"/g, '\\"');
          body.style.setProperty("--ff-care-blog-bg-image", 'url("' + safeUrl + '")');
        } else {
          body.style.removeProperty("--ff-care-blog-bg-image");
        }

        body.style.setProperty(
          "--ff-care-blog-bg-attachment",
          section.dataset.ffCareBlogParallax === "1" ? "fixed" : "scroll"
        );

        cleanups.push(() => {
          if (!document.body || document.body.dataset.ffCareBlogOwner !== ownerToken) return;
          document.body.removeAttribute("data-ff-care-blog-bg");
          document.body.removeAttribute("data-ff-care-blog-owner");
          document.body.style.removeProperty("--ff-care-blog-bg-image");
          document.body.style.removeProperty("--ff-care-blog-bg-attachment");
        });
      }

      const shell = section.querySelector(".ff-care-filters-shell");
      const toggle = shell ? shell.querySelector(".ff-care-filters-toggle") : null;
      if (shell && toggle) {
        const onClick = function () {
          const isOpen = shell.classList.toggle("ff-care-filters-shell--open");
          toggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
        };
        toggle.addEventListener("click", onClick);
        cleanups.push(() => toggle.removeEventListener("click", onClick));
      }

      registerCleanup(section, () => {
        cleanups.forEach((cleanup) => {
          try {
            cleanup();
          } catch (_) {}
        });
        delete section.dataset.ffCareBlogInit;
      });
    });
  }

  function initFfRichTextCollapsibles(root) {
  qsa(root, "[data-ff-richtext-collapsible]").forEach((section) => {
    if (section.dataset.ffRichtextCollapsibleInit === "1") return;
    section.dataset.ffRichtextCollapsibleInit = "1";

    const readMore = section.dataset.ffReadMore || "Read more";
    const readLess = section.dataset.ffReadLess || "Show less";
    const cleanups = [];

    section.querySelectorAll("[data-collapsible-toggle]").forEach((toggle) => {
      const container = toggle.closest(".ff-collapsible");
      if (!container) return;

      const content = container.querySelector("[data-collapsible-content]");
      if (!content) return;

      let expanded = container.classList.contains("is-expanded");
      let resizeTimer = null;

      const syncButton = () => {
        toggle.setAttribute("aria-expanded", expanded ? "true" : "false");
        toggle.textContent = expanded ? readLess : readMore;
      };

      const measureOverflow = () => {
        const wasExpanded = container.classList.contains("is-expanded");

        container.classList.remove("is-expanded");
        container.classList.remove("is-not-collapsible");
        container.classList.add("is-collapsible");

        const canCollapse = content.scrollHeight > content.clientHeight + 4;

        container.classList.toggle("is-collapsible", canCollapse);
        container.classList.toggle("is-not-collapsible", !canCollapse);

        if (wasExpanded && canCollapse) {
          container.classList.add("is-expanded");
        }

        if (!canCollapse) {
          expanded = false;
          container.classList.remove("is-expanded");
        }

        toggle.hidden = !canCollapse;
        syncButton();
      };

      const scheduleMeasure = () => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(() => {
          window.requestAnimationFrame(measureOverflow);
        }, 80);
      };

      const onClick = () => {
        expanded = !expanded;
        container.classList.toggle("is-expanded", expanded);
        syncButton();
      };

      const onResize = () => {
        scheduleMeasure();
      };

      toggle.addEventListener("click", onClick);
      window.addEventListener("resize", onResize, { passive: true });

      let resizeObserver = null;

      if ("ResizeObserver" in window) {
        resizeObserver = new ResizeObserver(scheduleMeasure);
        resizeObserver.observe(content);
      }

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(scheduleMeasure).catch(() => {});
      }

      window.requestAnimationFrame(measureOverflow);

      cleanups.push(() => {
        toggle.removeEventListener("click", onClick);
        window.removeEventListener("resize", onResize);
        window.clearTimeout(resizeTimer);

        if (resizeObserver) {
          try {
            resizeObserver.disconnect();
          } catch (_) {}
        }
      });
    });

    registerCleanup(section, () => {
      cleanups.forEach((cleanup) => {
        try {
          cleanup();
        } catch (_) {}
      });
      delete section.dataset.ffRichtextCollapsibleInit;
    });
  });
}
  /* =========================================
     PRODUCT CARD: MOBILE TAP REVEAL
     - first tap reveals quick action + secondary image
     - second tap follows product link normally
     - action buttons/forms remain usable
  ========================================== */
  let ffProductCardTapRevealInit = false;

  function initFfProductCardTapReveal() {
    if (ffProductCardTapRevealInit) return;
    ffProductCardTapRevealInit = true;

    const touchQuery = window.matchMedia("(hover: none), (pointer: coarse)");
    const cardSelector = 'product-card[data-ff-card-tap-reveal="true"]';

    const closeCards = (activeCard) => {
      document.querySelectorAll(`${cardSelector}.is-ff-card-tapped`).forEach((card) => {
        if (card !== activeCard) {
          card.classList.remove("is-ff-card-tapped");
        }
      });
    };

    const isActionTarget = (target) => {
      return !!(
        target &&
        target.closest &&
        target.closest(
          '[data-ff-card-action], button, input, select, textarea, summary, [role="button"], [aria-controls]'
        )
      );
    };

    document.addEventListener(
      "click",
      (event) => {
        if (!touchQuery.matches) return;

        const card = event.target.closest(cardSelector);

        if (!card) {
          closeCards(null);
          return;
        }

        if (isActionTarget(event.target)) return;

        if (!card.classList.contains("is-ff-card-tapped")) {
          event.preventDefault();
          event.stopPropagation();

          closeCards(card);

          const secondary = card.querySelector(
            'img.product-card__image--secondary[data-src], img.product-card__image--secondary[data-srcset], img.product-card__image--secondary[data-sizes]'
          );

          if (secondary) {
            secondary.dispatchEvent(new Event("ff:load-secondary", { bubbles: true }));
          }

          card.classList.add("is-ff-card-tapped");
        }
      },
      true
    );

    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      closeCards(null);
    });
  }

/* ========== LAZY MODALS (FF) ========== */

let ffLazyModalDelegationInit = false;

function initFfLazyModals() {
  if (ffLazyModalDelegationInit) return;
  ffLazyModalDelegationInit = true;

  async function loadLazyModal(modal) {
    if (!modal) return;
    if (modal.dataset.ffModalLoaded === "true") return;
    if (modal.dataset.ffModalLoading === "true") return;

    const src = modal.dataset.ffModalSrc;
    const selector = modal.dataset.ffModalSelector || "#ff-shipping-table-source";
    const body = modal.querySelector("[data-ff-modal-body]");

    if (!src || !body) return;

    modal.dataset.ffModalLoading = "true";
    body.setAttribute("aria-busy", "true");

    try {
      const response = await fetch(src, {
        credentials: "same-origin",
        headers: { "X-Requested-With": "XMLHttpRequest" }
      });

      if (!response.ok) {
        throw new Error("HTTP " + response.status);
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const source = doc.querySelector(selector);

      if (!source) {
        throw new Error("Missing selector: " + selector);
      }

      body.innerHTML = source.innerHTML;

      // Wrap any injected prose tables the same way the rest of FF UI does
      wrapProseTables(modal);

      modal.dataset.ffModalLoaded = "true";
    } catch (error) {
      console.error("FF lazy modal failed:", error);
      body.innerHTML =
        '<p class="text-sm text-subdued">Shipping details could not be loaded. Please try again.</p>';
      modal.dataset.ffModalLoaded = "error";
    } finally {
      body.setAttribute("aria-busy", "false");
      delete modal.dataset.ffModalLoading;
    }
  }

  function getLazyModalFromTriggerTarget(target) {
    if (!target || !target.closest) return null;

    const trigger = target.closest("[aria-controls]");
    if (!trigger) return null;

    const modalId = trigger.getAttribute("aria-controls");
    if (!modalId) return null;

    const modal = document.getElementById(modalId);
    if (!modal) return null;
    if (modal.dataset.ffLazyModal !== "true") return null;

    return modal;
  }

  document.addEventListener("click", function (event) {
    const modal = getLazyModalFromTriggerTarget(event.target);
    if (modal) loadLazyModal(modal);
  });
}


  /* ========== SCROLL STORY (FF) ========== */

function initFfScrollStory(root) {
  qsa(root, "[data-ff-scroll-story]").forEach((section) => {
    if (section.dataset.ffScrollStoryInit === "true") return;

    const scroller = section.querySelector("[data-ffss-scroller]");
    const stage = section.querySelector("[data-ffss-stage]");
    const introCard = section.querySelector("[data-ffss-intro-card]");
    const stepCards = Array.from(section.querySelectorAll("[data-ffss-step-card]"));
    const stepMedia = Array.from(section.querySelectorAll("[data-ffss-step-media]"));

    const mobileStepper = section.querySelector("[data-ffss-mobile-stepper]");
    const mobileSlides = Array.from(section.querySelectorAll("[data-ffss-mobile-slide]"));
    const mobilePrev = section.querySelector("[data-ffss-mobile-prev]");
    const mobileNext = section.querySelector("[data-ffss-mobile-next]");
    const mobileDots = Array.from(section.querySelectorAll("[data-ffss-mobile-dot]"));
    const mobileCurrent = section.querySelector("[data-ffss-mobile-current]");
    const mobileTotal = section.querySelector("[data-ffss-mobile-total]");

    if (!scroller || !stage || !introCard || !stepCards.length) return;

    section
      .querySelectorAll(".ff-scroll-story__desktop .ff-scroll-story__step-image, .ff-scroll-story__desktop .ff-scroll-story__base-image")
      .forEach((img) => {
        try {
          img.loading = "eager";
          img.decoding = "async";

          if ("fetchPriority" in img) {
            img.fetchPriority = "low";
          }

          if (typeof img.decode === "function") {
            img.decode().catch(() => {});
          }
        } catch (_) {}
      });

    section.dataset.ffScrollStoryInit = "true";

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const desktop = window.matchMedia("(min-width: 1000px)");
    const stateCount = stepCards.length + 1;

    let raf = null;
    let currentState = -1;
    let mobileIndex = 0;
    let touchStartX = 0;
    let touchEndX = 0;

    const shouldEnhanceDesktop = () => desktop.matches && !reduceMotion.matches;

    const setInert = (element, inertState) => {
      if (!element) return;

      element.setAttribute("aria-hidden", inertState ? "true" : "false");

      try {
        element.inert = inertState;
      } catch (_) {}
    };

    const setActiveState = (stateIndex, scrollToState = false) => {
      stateIndex = Math.min(Math.max(stateIndex, 0), stateCount - 1);
      currentState = stateIndex;

      const introActive = stateIndex === 0;
      const activeStepIndex = stateIndex - 1;

      section.classList.toggle("is-intro-state", introActive);
      section.classList.toggle("is-step-state", !introActive);

      setInert(introCard, !introActive);

      stepCards.forEach((card, index) => {
        const isActive = !introActive && index === activeStepIndex;
        card.classList.toggle("is-active", isActive);
        setInert(card, !isActive);
      });

      stepMedia.forEach((media, index) => {
        const isActive = !introActive && index === activeStepIndex;
        media.classList.toggle("is-active", isActive);
        media.setAttribute("aria-hidden", isActive ? "false" : "true");
      });

      if (scrollToState && shouldEnhanceDesktop()) {
        const stickyTop = parseFloat(getComputedStyle(stage).top) || 0;
        const maxScroll = Math.max(1, scroller.offsetHeight - stage.offsetHeight);
        const targetProgress = stateCount === 1 ? 0 : stateIndex / (stateCount - 1);
        const sectionTop = window.scrollY + scroller.getBoundingClientRect().top;
        const targetY = sectionTop - stickyTop + maxScroll * targetProgress;

        window.scrollTo({
          top: targetY,
          behavior: "smooth"
        });
      }
    };

    const updateDesktop = () => {
      raf = null;

      if (!shouldEnhanceDesktop()) {
        setActiveState(0);
        return;
      }

      const stickyTop = parseFloat(getComputedStyle(stage).top) || 0;
      const rect = scroller.getBoundingClientRect();
      const maxScroll = Math.max(1, scroller.offsetHeight - stage.offsetHeight);
      const progress = Math.min(Math.max((stickyTop - rect.top) / maxScroll, 0), 1);
      const nextState = Math.min(Math.max(Math.floor(progress * stateCount), 0), stateCount - 1);

      if (nextState !== currentState) {
        setActiveState(nextState);
      }
    };

    const requestDesktopUpdate = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(updateDesktop);
    };

    const setMobileSlide = (index) => {
      if (!mobileSlides.length) return;

      mobileIndex = Math.min(Math.max(index, 0), mobileSlides.length - 1);

      mobileSlides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === mobileIndex;
        slide.classList.toggle("is-active", isActive);
        slide.hidden = !isActive;
        slide.setAttribute("aria-hidden", isActive ? "false" : "true");
      });

      mobileDots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === mobileIndex;
        dot.classList.toggle("is-active", isActive);
        dot.setAttribute("aria-current", isActive ? "true" : "false");
        dot.setAttribute("aria-selected", isActive ? "true" : "false");
        dot.tabIndex = isActive ? 0 : -1;
      });

      if (mobilePrev) mobilePrev.disabled = mobileIndex === 0;
      if (mobileNext) mobileNext.disabled = mobileIndex === mobileSlides.length - 1;

      if (mobileCurrent) mobileCurrent.textContent = String(mobileIndex + 1);
      if (mobileTotal) mobileTotal.textContent = String(mobileSlides.length);
    };

    const goMobilePrev = () => setMobileSlide(mobileIndex - 1);
    const goMobileNext = () => setMobileSlide(mobileIndex + 1);

    const onMobileKeydown = (event) => {
      if (!mobileStepper || desktop.matches) return;

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goMobilePrev();
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        goMobileNext();
      }
    };

    const onTouchStart = (event) => {
      if (!mobileStepper || desktop.matches) return;
      touchStartX = event.changedTouches[0].screenX;
    };

    const onTouchEnd = (event) => {
      if (!mobileStepper || desktop.matches) return;

      touchEndX = event.changedTouches[0].screenX;
      const delta = touchStartX - touchEndX;

      if (Math.abs(delta) < 45) return;

      if (delta > 0) {
        goMobileNext();
      } else {
        goMobilePrev();
      }
    };

    const onBlockSelect = (event) => {
      const blockId = event.detail && event.detail.blockId;
      if (!blockId) return;

      const desktopCard = section.querySelector(`[data-ffss-block-id="${blockId}"]`);
      const mobileSlide = section.querySelector(`[data-ffss-mobile-block-id="${blockId}"]`);

      if (desktop.matches && desktopCard) {
        const index = stepCards.indexOf(desktopCard);
        if (index >= 0) setActiveState(index + 1, true);
        return;
      }

      if (!desktop.matches && mobileSlide) {
        const index = mobileSlides.indexOf(mobileSlide);
        if (index >= 0) setMobileSlide(index);
      }
    };

    window.addEventListener("scroll", requestDesktopUpdate, { passive: true });
    window.addEventListener("resize", requestDesktopUpdate);

    if (reduceMotion.addEventListener) {
      reduceMotion.addEventListener("change", requestDesktopUpdate);
    } else if (reduceMotion.addListener) {
      reduceMotion.addListener(requestDesktopUpdate);
    }

    if (desktop.addEventListener) {
      desktop.addEventListener("change", requestDesktopUpdate);
    } else if (desktop.addListener) {
      desktop.addListener(requestDesktopUpdate);
    }

    if (mobilePrev) mobilePrev.addEventListener("click", goMobilePrev);
    if (mobileNext) mobileNext.addEventListener("click", goMobileNext);

    mobileDots.forEach((dot, index) => {
      dot.addEventListener("click", () => setMobileSlide(index));
    });

    if (mobileStepper) {
      mobileStepper.addEventListener("keydown", onMobileKeydown);
      mobileStepper.addEventListener("touchstart", onTouchStart, { passive: true });
      mobileStepper.addEventListener("touchend", onTouchEnd, { passive: true });
    }

    if (window.Shopify && Shopify.designMode) {
      document.addEventListener("shopify:block:select", onBlockSelect);
    }

    setActiveState(0);
    setMobileSlide(0);
    requestDesktopUpdate();

    registerCleanup(section, () => {
      window.removeEventListener("scroll", requestDesktopUpdate);
      window.removeEventListener("resize", requestDesktopUpdate);

      if (reduceMotion.removeEventListener) {
        reduceMotion.removeEventListener("change", requestDesktopUpdate);
      } else if (reduceMotion.removeListener) {
        reduceMotion.removeListener(requestDesktopUpdate);
      }

      if (desktop.removeEventListener) {
        desktop.removeEventListener("change", requestDesktopUpdate);
      } else if (desktop.removeListener) {
        desktop.removeListener(requestDesktopUpdate);
      }

      if (mobilePrev) mobilePrev.removeEventListener("click", goMobilePrev);
      if (mobileNext) mobileNext.removeEventListener("click", goMobileNext);

      if (mobileStepper) {
        mobileStepper.removeEventListener("keydown", onMobileKeydown);
        mobileStepper.removeEventListener("touchstart", onTouchStart);
        mobileStepper.removeEventListener("touchend", onTouchEnd);
      }

      if (window.Shopify && Shopify.designMode) {
        document.removeEventListener("shopify:block:select", onBlockSelect);
      }

      if (raf) {
        window.cancelAnimationFrame(raf);
      }

      stepCards.forEach((card) => {
        card.classList.remove("is-active");
        setInert(card, false);
      });

      stepMedia.forEach((media) => {
        media.classList.remove("is-active");
        media.setAttribute("aria-hidden", "true");
      });

      introCard.removeAttribute("aria-hidden");

      try {
        introCard.inert = false;
      } catch (_) {}

      section.classList.remove("is-intro-state", "is-step-state");
      delete section.dataset.ffScrollStoryInit;
    });
  });
}


  /* ========== INIT / HOOKS ========== */

function initAll(root) {
  root = root || document;
  initFfLazyModals();
  wrapProseTables(root);
  markFfPresentationShells(root);
  initFfRecentlyViewedSections(root);
  initFfDeferredSecondaryImages(root);
  initFfProductCardTapReveal();
  initFfLightParallax(root);
  initFfTabs(root);
  initFfAccordions(root);
  initFfAnnouncementBars(root);
  initFfHeader(root);
  initFfMainProduct(root);
  initFfBackToTop(root);
  initFfQuickAccess(root);
  initFfTabsParallax(root);
  initFfParallaxMultiColumn(root);
  initFfCareBlog(root);
  initFfRichTextCollapsibles(root);
  initFfNewsletterPopupSuccess(root);
  initFfHeaderSidebarAccordions(root);
  initFfCollectionDescriptionCollapsibles(root);
  initFfCollectionBanners(root);
  initFfImageTextParallax(root);
  initFfScrollStory(root);
  initFfFilterModals(root);
  initFfInlineActionHelpers(root);
  initFfPageUtilities(root);
}

  window.FF = window.FF || {};
  window.FF.initFfUI = initAll;

  // Initial load
  if (document.readyState === "interactive" || document.readyState === "complete") {
    initAll(document);
  } else {
    document.addEventListener("DOMContentLoaded", function () {
      initAll(document);
    });
  }

  // Shopify editor section reload
  document.addEventListener("shopify:section:load", function (event) {
    initAll(event.target);
  });

  document.addEventListener("shopify:section:unload", function (event) {
    runCleanup(event.target);
  });

  // Catch dynamic injections (product rerender swaps, cart drawer refresh, etc.)
  let scheduled = false;
  let pendingRoots = new Set();

  function normalizePendingRoots(roots) {
    if (!roots.length) return roots;
    if (roots.includes(document)) return [document];

    return roots.filter((root, index) => {
      return !roots.some((candidate, candidateIndex) => {
        if (candidateIndex === index) return false;
        if (candidate === document) return true;
        return !!(candidate && candidate.contains && candidate.contains(root));
      });
    });
  }

  function scheduleFlush() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      const roots = normalizePendingRoots(Array.from(pendingRoots));
      pendingRoots.clear();
      roots.forEach((r) => initAll(r));
    });
  }

  const mo = new MutationObserver(function (mutations) {
    mutations.forEach((m) => {
      m.addedNodes.forEach((node) => {
        collectInitRoot(node, pendingRoots);
      });

      m.removedNodes.forEach((node) => {
        if (nodeMatchesOrContains(node, "[data-ff-cleanup-root]")) runCleanup(node);
      });
    });

    if (pendingRoots.size) scheduleFlush();
  });

  function startObserver() {
    if (!document.body) return;
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.body) startObserver();
  else document.addEventListener("DOMContentLoaded", startObserver);
})();
